import { formatApiErrorDetail } from "@/lib/api";
import { Card, FinShell, NumField, SelectField, Stat, SavedBar, Assumptions, money, fmtDate, today } from "@/components/finance/fin";
import { BalanceChart, ScheduleTable, AcceleratorStats, PaymentHistory, PlannedExtras } from "@/components/finance/LoanParts";
import { useLoanTool } from "@/components/finance/useLoanTool";

const FREQS = [["monthly", "Monthly"], ["semi_monthly", "Semi-monthly"], ["biweekly", "Biweekly"], ["weekly", "Weekly"]];
const COMP = [["monthly", "Monthly compounding (most dealer/bank loans)"], ["daily", "Daily simple interest (actual days)"]];
const DEFAULTS = { price: 45000, down: 5000, trade_in: 0, tax_pct: 13, fees: 0, rate: 6.99, term_months: 72, start_date: today(),
  balance: "", remaining_months: "", frequency: "monthly", compounding: "monthly", payment: "", planned_extras: [] };

function TermCompare({ rows, current }) {
  if (!rows) return null;
  return (
    <Card title="Compare financing terms" subtitle="Same amount financed and rate" testId="fin-term-compare">
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-1">Term</th><th className="text-right">Payment</th><th className="text-right">Total interest</th><th className="text-right">Total paid</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.term_months} className={`border-t border-border/60 ${Number(current) === r.term_months ? "text-primary font-semibold" : ""}`} data-testid={`fin-term-${r.term_months}`}>
            <td className="py-1.5">{r.term_months} months</td><td className="text-right">{money(r.regular_payment)}</td><td className="text-right">{money(r.total_interest)}</td><td className="text-right">{money(r.total_paid)}</td>
          </tr>))}</tbody>
      </table></div>
    </Card>
  );
}

export default function VehiclePage() {
  const t = useLoanTool("vehicle", DEFAULTS);
  const i = t.inputs;
  const res = t.calc.data;
  const acc = res?.accelerator;
  const cur = acc?.current;
  return (
    <FinShell title="Vehicle financing & payoff accelerator" subtitle="Payments, total cost of financing, term comparison and extra payments for car, truck or RV loans." testId="vehicle-page">
      <SavedBar kind="vehicle" selected={t.pid} onSelect={t.select} inputs={i} onReset={t.reset} defaultName="My vehicle loan" />
      <Card title="Your vehicle loan" testId="vehicle-inputs">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <NumField id="veh-price" label="Vehicle purchase price" value={i.price} onChange={t.set("price")} />
          <NumField id="veh-down" label="Down payment" value={i.down} onChange={t.set("down")} />
          <NumField id="veh-trade" label="Trade-in value" value={i.trade_in} onChange={t.set("trade_in")} />
          <NumField id="veh-tax" label="Sales tax (%)" value={i.tax_pct} onChange={t.set("tax_pct")} hint="Ontario HST 13%, charged on price minus trade-in" />
          <NumField id="veh-fees" label="Fees (licensing, admin)" value={i.fees} onChange={t.set("fees")} />
          <NumField id="veh-rate" label="Interest rate (%)" value={i.rate} onChange={t.set("rate")} />
          <NumField id="veh-term" label="Loan term (months)" value={i.term_months} onChange={t.set("term_months")} step="1" />
          <NumField id="veh-start" label="First payment date" type="date" value={i.start_date} onChange={t.set("start_date")} />
          <SelectField id="veh-frequency" label="Payment frequency" value={i.frequency} onChange={t.set("frequency")} options={FREQS} />
          <SelectField id="veh-compounding" label="Interest calculation" value={i.compounding} onChange={t.set("compounding")} options={COMP} />
          <NumField id="veh-balance" label="Current outstanding balance (existing loan)" value={i.balance} onChange={t.set("balance")} hint="Leave blank for a new purchase" />
          <NumField id="veh-remaining" label="Remaining term (months)" value={i.remaining_months} onChange={t.set("remaining_months")} step="1" />
        </div>
      </Card>
      {t.calc.error && <p className="text-sm text-red-400" data-testid="fin-calc-error">{formatApiErrorDetail(t.calc.error.response?.data?.detail)}</p>}
      {cur && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vehicle-results">
            <Stat label="Amount financed" value={money(res.amount_financed)} testId="veh-result-financed" sub={`incl. ${money(res.sales_tax)} tax`} />
            <Stat label="Regular payment" value={money(cur.regular_payment)} testId="veh-result-payment" />
            <Stat label="Principal / interest (1st)" value={`${money(cur.first_principal)} / ${money(cur.first_interest)}`} />
            <Stat label="Total interest" value={money(cur.total_interest)} testId="veh-result-interest" />
            <Stat label="Cost of financing (no extras)" value={money(res.total_cost_of_financing)} />
            <Stat label="Est. balance today" value={money(res.estimated_balance_today)} />
            <Stat label="Final payment date" value={fmtDate(cur.payoff_date)} testId="veh-result-payoff" />
            <Stat label="Final payment" value={money(cur.final_payment)} />
          </div>
          <Card title="Balance over time"><BalanceChart schedule={cur.schedule} /></Card>
          <TermCompare rows={res.term_comparison} current={i.term_months} />
          <AcceleratorStats acc={acc} balanceToday={res.estimated_balance_today} />
          <PaymentHistory pid={t.pid} txns={t.txns} />
          <PlannedExtras list={i.planned_extras || []} onChange={t.set("planned_extras")} sim={res.simulation} />
          <ScheduleTable schedule={cur.schedule} />
        </>
      )}
      <Assumptions>Most Canadian vehicle loans are open loans charging monthly-compounded or daily simple interest; check your contract for prepayment rules or penalties. Extra payments go straight to principal and your regular payment stays the same.</Assumptions>
    </FinShell>
  );
}
